import React, { useMemo } from 'react';
import { ReactComponent as Drag } from 'src/Images/icons/common/drag.svg';
import { ReactComponent as Close } from 'src/Images/icons/common/close.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { useSelector } from 'react-redux';

export default function TableRightDrawer({
  toggleDrawer,
  fieldFilter,
  selectedFieldFilter,
  handleSelectedFieldFilter,
  setDraggedFieldOrder,
  fieldOrder,
}) {
  const themeColor = useSelector(
    (state) => state.AuthReducer?.userData?.tenant?.theme,
  );

  /**
   * orderedFields (derived, not stored)
   *
   * 1. Selected fields → ordered by draggedFieldOrder
   * 2. Unselected fields → appended alphabetically (backend order)
   */
  const orderedFields = useMemo(() => {
    const fieldMap = new Map(
      fieldFilter.map(({ value, label }) => [value, label]),
    );

    const selectedSet = new Set(selectedFieldFilter);

    const selected = fieldOrder
      .filter((id) => fieldMap.has(id))
      .map((id) => ({
        id,
        name: fieldMap.get(id),
        selected: true,
      }));

    const unselected = fieldFilter
      .filter((f) => !selectedSet.has(f.value))
      .map(({ value, label }) => ({
        id: value,
        name: label,
        selected: false,
      }));

    return [...selected, ...unselected];
  }, [fieldFilter, fieldOrder, selectedFieldFilter]);

  const handleDrop = (e, dropIndex) => {
    const draggedIndex = Number(e.dataTransfer.getData('draggedIndex'));
    if (draggedIndex === dropIndex) return;

    const selectedFields = orderedFields.filter((f) => f.selected);

    const updated = [...selectedFields];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, moved);

    setDraggedFieldOrder(updated.map((f) => f.id));
  };

  const handleCheckboxChange = (e, id) => {
    handleSelectedFieldFilter(e, id);

    setDraggedFieldOrder((prev) => {
      if (e.target.checked) {
        // merge existing selected fields + new id
        const base = prev.length > 0 ? prev : selectedFieldFilter;
        return [...new Set([...base, id])];
      }
      return prev.filter((v) => v !== id);
    });
  };

  const checkedFields = orderedFields.filter((f) =>
    selectedFieldFilter.includes(f.id),
  );
  const uncheckedFields = orderedFields.filter(
    (f) => !selectedFieldFilter.includes(f.id),
  );

  return (
    <div data-testid="organizer-drawer">
      <div className="px-3 py-2 border-bottom div-space-between div-align-center">
        <span className="text-sm text-bold">Organizer</span>
        <button
          className="icon-btn-none"
          data-testid="organizer-close-btn"
          onClick={toggleDrawer(false)}
          aria-label="Close organizer"
        >
          <Close className="icon-size-sm" color={getColorCode(themeColor)} />
        </button>
      </div>

      <div className="px-3 py-3 div-flex-column rg-20">
        {checkedFields.map(({ id, name }, index) => (
          <div
            key={id}
            draggable
            className="div-space-between"
            onDragStart={(e) => e.dataTransfer.setData('draggedIndex', index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="div-flex-row cg-10">
              <input
                type="checkbox"
                aria-label={`Show ${name}`}
                checked
                onChange={(e) => handleCheckboxChange(e, id)}
              />
              <span className="text-sm">{name}</span>
            </div>
            <Drag className="icon-size-lg" color="#DBDBDB" />
          </div>
        ))}

        {checkedFields.length > 0 && <div className="border-bottom" />}

        {uncheckedFields.map(({ id, name }) => (
          <div key={id} className="div-flex-row cg-10">
            <input
              type="checkbox"
              aria-label={`Show ${name}`}
              checked={false}
              onChange={(e) => handleCheckboxChange(e, id)}
            />
            <span className="text-sm">{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
